import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { BranchDetail } from '@/features/local-configuration/types/local-configuration-api.types';
import { STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { storage } from '@/shared/utils/storage';

import { normalizePersistedUser } from '@/features/auth/utils/normalize-persisted-user';

import type {
  AuthState,
  LoginMode,
  Pro20BranchSummary,
  Pro20User,
} from '../types/auth.types';

const loadPersistedAuth = (): Pick<
  AuthState,
  | 'user'
  | 'accessToken'
  | 'branchCode'
  | 'branches'
  | 'branchDetail'
  | 'isAuthenticated'
  | 'loginMode'
> => {
  const accessToken = storage.getString(STORAGE_KEYS.ACCESS_TOKEN);
  const user = normalizePersistedUser(storage.get(STORAGE_KEYS.USER));
  const branchCode = storage.getString(STORAGE_KEYS.BRANCH_CODE);
  const branches =
    (storage.get(STORAGE_KEYS.BRANCHES) as Pro20BranchSummary[] | null) ?? [];
  const branchDetail = storage.get(
    STORAGE_KEYS.BRANCH_DETAIL,
  ) as BranchDetail | null;
  return {
    user,
    accessToken,
    branchCode,
    branches,
    branchDetail,
    isAuthenticated: Boolean(accessToken && user),
    loginMode: 'userCode',
  };
};

const initialState: AuthState = {
  ...loadPersistedAuth(),
  isInitialized: false,
};

interface SetSessionPayload {
  user: Pro20User;
  accessToken: string;
  branchCode: string | null;
  branches?: Pro20BranchSummary[];
  loginMode: LoginMode;
  rememberMe?: boolean;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSession: (state, action: PayloadAction<SetSessionPayload>) => {
      const {
        user,
        accessToken,
        branchCode,
        branches = [],
        loginMode,
        rememberMe = true,
      } = action.payload;

      state.user = user;
      state.accessToken = accessToken;
      state.branchCode = branchCode;
      state.branches = branches;
      state.loginMode = loginMode;
      state.isAuthenticated = true;

      if (rememberMe) {
        storage.setString(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        storage.set(STORAGE_KEYS.USER, user);
        storage.set(STORAGE_KEYS.BRANCHES, branches);
        storage.setString(STORAGE_KEYS.LOGIN_MODE, loginMode);
        if (branchCode) {
          storage.setString(STORAGE_KEYS.BRANCH_CODE, branchCode);
        }
        storage.setString(STORAGE_KEYS.REMEMBER_ME, 'true');
      }
    },

    setBranchDetail: (state, action: PayloadAction<BranchDetail | null>) => {
      state.branchDetail = action.payload;
      if (storage.getString(STORAGE_KEYS.REMEMBER_ME) === 'true') {
        if (action.payload) {
          storage.set(STORAGE_KEYS.BRANCH_DETAIL, action.payload);
        } else {
          storage.remove(STORAGE_KEYS.BRANCH_DETAIL);
        }
      }
    },

    setBranchCode: (state, action: PayloadAction<string | null>) => {
      state.branchCode = action.payload;
      if (
        action.payload &&
        storage.getString(STORAGE_KEYS.REMEMBER_ME) === 'true'
      ) {
        storage.setString(STORAGE_KEYS.BRANCH_CODE, action.payload);
      }
    },

    setActiveBranch: (state, action: PayloadAction<string>) => {
      state.branchCode = action.payload;
      if (state.user) {
        state.user.branchCode = action.payload;
      }

      if (storage.getString(STORAGE_KEYS.REMEMBER_ME) === 'true') {
        storage.setString(STORAGE_KEYS.BRANCH_CODE, action.payload);
        if (state.user) {
          storage.set(STORAGE_KEYS.USER, state.user);
        }
      }
    },

    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.branchCode = null;
      state.branches = [];
      state.branchDetail = null;
      state.isAuthenticated = false;

      storage.clear([
        STORAGE_KEYS.ACCESS_TOKEN,
        STORAGE_KEYS.USER,
        STORAGE_KEYS.BRANCH_CODE,
        STORAGE_KEYS.BRANCHES,
        STORAGE_KEYS.BRANCH_DETAIL,
        STORAGE_KEYS.REMEMBER_ME,
        STORAGE_KEYS.LOGIN_MODE,
      ]);
    },

    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
  },
  selectors: {
    selectAuth: (state) => state,
    selectUser: (state) => state.user,
    selectIsAuthenticated: (state) => state.isAuthenticated,
    selectAccessToken: (state) => state.accessToken,
    selectBranchCode: (state) => state.branchCode,
    selectBranchDetail: (state) => state.branchDetail,
    selectBranches: (state) => state.branches,
    selectIsInitialized: (state) => state.isInitialized,
    selectLoginMode: (state) => state.loginMode,
  },
});

export const {
  setSession,
  setBranchDetail,
  setBranchCode,
  setActiveBranch,
  logout,
  setInitialized,
} = authSlice.actions;

export const {
  selectAuth,
  selectUser,
  selectIsAuthenticated,
  selectAccessToken,
  selectBranchCode,
  selectBranchDetail,
  selectBranches,
  selectIsInitialized,
  selectLoginMode,
} = authSlice.selectors;

export default authSlice.reducer;
